import { Controller } from '@hotwired/stimulus'
import L from 'leaflet'

const addressMarkerIcon =
  `
  <svg viewBox="0 0 49 48" xmlns="http://www.w3.org/2000/svg">
    <defs></defs>
    <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
      <g transform="translate(-287.000000, -1776.000000)" fill="currentColor" fill-rule="nonzero">
        <g id="Prato" transform="translate(48.000000, 1708.000000)">
          <g id="Alerta-endere\xE7o" transform="translate(239.000000, 68.000000)">
            <path d="M25,45 C13,36.9607503 7,28.9607503 7,21 C7,11.0588745 15.0588745,3 25,3 C34.9411255,3 43,11.0588745 43,21 C43,28.9607503 37,36.9607503 25,45 Z M25,29 C29.418278,29 33,25.418278 33,21 C33,16.581722 29.418278,13 25,13 C20.581722,13 17,16.581722 17,21 C17,25.418278 20.581722,29 25,29 Z" id="Combined-Shape"></path>
          </g>
        </g>
      </g>
    </g>
  </svg>
  `

const addressMarkerPopup =
  `
  <p class="font-sans text-xs text-center text-gray-500"><span class="text-base font-medium text-gray-900">Você está aqui?</span><br />Ajuste a localização</p>
  `

const addressMarkerPopupByPrecision = {
  exact: addressMarkerPopup,
  approximate:
    `
    <p class="font-sans text-xs text-center text-gray-500"><span class="text-base font-medium text-gray-900">Confira o local de entrega</span><br />Arraste o mapa até a sua casa</p>
    `,
  unknown:
    `
    <p class="font-sans text-xs text-center text-gray-500"><span class="text-base font-medium text-gray-900">Não encontramos o endereço</span><br />Mova o mapa até o local de entrega</p>
    `
}

const userMarkerIcon =
  `
  <div class="relative">
    <div class="absolute inset-0 w-5 h-5 bg-blue-600 rounded-full opacity-75 animate-ping"></div>
    <div class="w-5 h-5 bg-blue-600 rounded-full border-2 border-white"></div>
  </div>
  `

const userLocationIcon =
  `
  <svg xmlns = "http://www.w3.org/2000/svg" viewBox = "0 0 24 24" fill = "currentColor" class="w-6 h-6" >
    <path d="M13 1L13.001 4.06201C16.6192 4.51365 19.4869 7.38163 19.9381 11L23 11V13L19.938 13.001C19.4864 16.6189 16.6189 19.4864 13.001 19.938L13 23H11L11 19.9381C7.38163 19.4869 4.51365 16.6192 4.06201 13.001L1 13V11L4.06189 11C4.51312 7.38129 7.38129 4.51312 11 4.06189L11 1H13ZM12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6ZM12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10Z" />
  </svg>
  `

const merchantDefaultIcon =
  `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6 text-dv-theme-700">
    <path d="M22 20V22H2V20H3V13.2422C1.79401 12.435 1 11.0602 1 9.5C1 8.67286 1.22443 7.87621 1.63322 7.19746L4.3453 2.5C4.52393 2.1906 4.85406 2 5.21132 2H18.7887C19.1459 2 19.4761 2.1906 19.6547 2.5L22.3575 7.18172C22.7756 7.87621 23 8.67286 23 9.5C23 11.0602 22.206 12.435 21 13.2422V20H22ZM5.78865 4L3.35598 8.21321C3.12409 8.59843 3 9.0389 3 9.5C3 10.8807 4.11929 12 5.5 12C6.53096 12 7.44467 11.3703 7.82179 10.4295C8.1574 9.59223 9.3426 9.59223 9.67821 10.4295C10.0553 11.3703 10.969 12 12 12C13.031 12 13.9447 11.3703 14.3218 10.4295C14.6574 9.59223 15.8426 9.59223 16.1782 10.4295C16.5553 11.3703 17.469 12 18.5 12C19.8807 12 21 10.8807 21 9.5C21 9.0389 20.8759 8.59843 20.6347 8.19746L18.2113 4H5.78865Z"></path>
  </svg>
  `

export default class extends Controller {
  static targets = ['map', 'submitButton', 'coordsInput', 'accuracyInput', 'reverseInput', 'geocodeInput']
  static values = {
    customerCoords: Array,
    merchantCoords: Array,
    merchantLogoUrl: String,
    precision: { type: String, default: 'exact' }
  }

  connect () {
    this.#initializeMap()
    this.#addTileLayer()
    this.#addAddressMarker()
    this.#addMerchantMarker()
    this.#addUserLocationMarker()
  }

  #initializeMap () {
    this.map = L.map(this.mapTarget, {
      zoomControl: false,
      minZoom: 6,
      maxZoom: 19,
      scrollWheelZoom: 'center',
      doubleClickZoom: 'center',
      touchZoom: 'center'
    }).setView(this.customerCoordsValue, 19)
  }

  #addTileLayer () {
    L.tileLayer(
      'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }
    ).addTo(this.map)
  }

  #addAddressMarker () {
    const normalIcon = this.#createIcon(addressMarkerIcon, 'delivery-vip-map-pin', [40, 45], [20, 37])
    const activeIcon = this.#createIcon(addressMarkerIcon, 'delivery-vip-map-pin delivery-vip-map-pin--active', [40, 45], [20, 37])
    const popupContent = this.#createPopup(addressMarkerPopupByPrecision[this.precisionValue] || addressMarkerPopup)

    this.addressMarker = L.marker(this.customerCoordsValue, { icon: normalIcon }).addTo(this.map)
    this.addressMarker.bindPopup(popupContent).openPopup()

    this.map.on('drag', () => this.#handleMapDrag(activeIcon))
    this.map.on('dragend', () => this.#handleMapDragEnd(normalIcon, popupContent))
  }

  #addMerchantMarker () {
    if (!this.hasMerchantCoordsValue) return

    if (this.hasMerchantLogoValue) {
      const merchantIcon = L.divIcon({
        className: 'bg-transparent border-none',
        html: `<img src="${this.merchantLogoUrlValue}" class="object-cover !w-10 !h-10 rounded-full" />`,
        iconSize: [45, 45]
      })

      L.marker(this.merchantCoordsValue, { icon: merchantIcon }).addTo(this.map)
    } else {
      const defaultIcon = L.divIcon({
        className: 'default-merchant-icon rounded-full !h-10 !w-10 flex items-center justify-center shadow bg-white/90',
        html: merchantDefaultIcon,
        iconSize: [24, 24]
      })

      L.marker(this.merchantCoordsValue, { icon: defaultIcon }).addTo(this.map)
    }
  }

  #addUserLocationMarker () {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => this.#handleUserLocation(position),
        (error) => {
          console.error('Erro na geolocalização do usuário:', error)
        }
      )
    }
  }

  #addUserLocationButton () {
    if (!this.userCoords) return
    this.buttonUserLocation = document.createElement('button')
    this.buttonUserLocation.innerHTML = userLocationIcon
    this.buttonUserLocation.className = 'absolute right-8 bottom-32 p-3 text-gray-500 bg-white rounded-full shadow hover:bg-white/90'
    this.buttonUserLocation.addEventListener('click', () => {
      this.addressMarker.setLatLng(this.userCoords)
      this.map.setView(this.userCoords, 19)
      this.#updateCoordinates(this.addressMarker.getLatLng(), this.userAccuracy)
    })
    this.element.appendChild(this.buttonUserLocation)
  }

  #handleUserLocation (position) {
    this.userCoords = [position.coords.latitude, position.coords.longitude]
    this.userAccuracy = position.coords.accuracy
    const userIcon = this.#createIcon(userMarkerIcon, '', [20, 20], [10, 10])
    L.marker(this.userCoords, { icon: userIcon }).addTo(this.map)

    this.#addUserLocationButton()
    this.addressMarker.setZIndexOffset(1000)
  }

  #handleMapDrag (activeIcon) {
    this.submitButtonTarget.classList.add('hidden')
    this.buttonUserLocation?.classList?.add('hidden')
    this.addressMarker.setIcon(activeIcon)
    this.map.closePopup()
    this.addressMarker.setLatLng(this.map.getCenter())
  }

  #handleMapDragEnd (normalIcon, popupContent) {
    this.submitButtonTarget.classList.remove('hidden')
    this.buttonUserLocation?.classList?.remove('hidden')
    this.addressMarker.setIcon(normalIcon)
    popupContent.setContent(addressMarkerPopup)
    this.addressMarker.bindPopup(popupContent).openPopup()
    this.#updateCoordinates(this.addressMarker.getLatLng())
  }

  #createIcon (html, className, iconSize, iconAnchor) {
    return L.divIcon({
      className, html, iconSize, iconAnchor
    })
  }

  #createPopup (content) {
    return L.popup({
      offset: [0, -40],
      closeButton: false,
      closeOnClick: false,
      closeOnEscapeKey: false,
      autoClose: false
    }).setContent(content)
  }

  #updateCoordinates (coords, accuracy = '') {
    this.coordsInputTarget.value = `POINT(${coords.lng} ${coords.lat})`
    if (this.hasAccuracyInputTarget) this.accuracyInputTarget.value = accuracy
    this.reverseInputTarget.value = 'true'
    this.geocodeInputTarget.value = 'true'
  }
}
